import { useEffect, useRef } from 'react';
import {
  type CameraSlice,
  createStoreHooks,
  getCoordinateTransformContext,
} from '../../../canvas/core';
import {
  calculateViewportInContent,
  getPagesInVerticalRange,
  isPageIntersectingViewport,
} from '../layout/pageGeometry';
import type { PageLayoutData } from '../layout/types';
import type { PageLayoutSlice } from '../store/types';

type UsePageVisibilityDeps = CameraSlice & PageLayoutSlice;

const { useStoreApi } = createStoreHooks<UsePageVisibilityDeps>();

const VIEWPORT_MARGIN = 200;

/**
 * Tracks which pages are visible in the viewport and updates their
 * visibility state in the store.
 *
 * Uses imperative `store.subscribe` instead of `useStore` to avoid
 * subscribing the calling component to camera state changes, which
 * would cause per-frame re-renders during pan/zoom.
 */
export const usePageVisibility = (pageLayouts: PageLayoutData[]) => {
  const store = useStoreApi();
  const pageLayoutsRef = useRef(pageLayouts);
  pageLayoutsRef.current = pageLayouts;

  const prevVisiblePagesRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    let rafId = 0;

    const runUpdate = () => {
      const state = store.getState();
      const transformCtx = getCoordinateTransformContext(state.canvas);
      const viewport = calculateViewportInContent(transformCtx);
      const margin = VIEWPORT_MARGIN / transformCtx.canvasTransform.zoom;
      const layouts = pageLayoutsRef.current;

      const candidatePages = getPagesInVerticalRange(
        layouts,
        viewport.y - margin,
        viewport.y + viewport.height + margin
      );

      const currentVisiblePages = new Set<number>();

      candidatePages.forEach(page => {
        const isVisible = isPageIntersectingViewport(page, viewport, margin);

        if (isVisible) {
          currentVisiblePages.add(page.pageId);
          if (!prevVisiblePagesRef.current.has(page.pageId)) {
            state.pageLayout.actions.setPageVisible(page.pageId, true);
          }
        }
      });

      prevVisiblePagesRef.current.forEach(pageId => {
        if (!currentVisiblePages.has(pageId)) {
          state.pageLayout.actions.setPageVisible(pageId, false);
        }
      });

      prevVisiblePagesRef.current = currentVisiblePages;
    };

    const scheduleUpdate = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(runUpdate);
    };

    // Run once immediately for initial visibility
    scheduleUpdate();

    // Only recompute when the camera transform changes — ignore unrelated
    // store writes (e.g. active page, translation layer, etc.).
    const unsubscribe = store.subscribe((state, prevState) => {
      if (state.canvas.transform !== prevState.canvas.transform) {
        scheduleUpdate();
      }
    });

    return () => {
      unsubscribe();
      cancelAnimationFrame(rafId);
    };
  }, [store]);
};
